import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { loadToken } from '../../localStorage';
import SubscriptionsForm from './SubscriptionsForm';
import {
  Paper,
  makeStyles,
  TableBody,
  TableRow,
  TableCell,
  Toolbar,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Typography,
  Box,
} from '@material-ui/core';
import useTable from '../../components/useTable';
import Controls from '../../components/controls/Controls';
import { Search } from '@material-ui/icons';
import AddIcon from '@material-ui/icons/Add';
import Popup from '../../components/Popup';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import CloseIcon from '@material-ui/icons/Close';
import Notification from '../../components/Notification';
import ConfirmDialog from '../../components/ConfirmDialog';
import { removeToken, checkUTF8 } from '../../utils/subscriptionsService';
import { getAppData, invalidateAppData } from '../../utils/appDataService';
import Copyright from '../../components/Copyright';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiCircularProgress-root': {
      'margin-left': '50%',
      marginTop: '20px',
      width: '100%',
    },
  },
  '.MuiCircularProgress-root': {
    width: '100%',
  },
  pageContent: {
    margin: theme.spacing(5),
    padding: theme.spacing(3),
  },
  searchInput: {
    width: '75%',
  },
  newButton: {
    position: 'absolute',
    right: '10px',
  },
  inValidCert: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const headCells = [
  { id: 'libelleRessource', label: 'Libellé' },
  { id: 'uaiEtab', label: 'UAI' },
  { id: 'debutValidite', label: 'Début' },
  { id: 'finValidite', label: 'Fin' },
  { id: 'publicCible', label: 'Cible' },
  { id: 'nbLicenses', label: 'Licences' },
  { id: 'codeProjetRessource', label: 'Code projet ressource' },
  { id: 'actions', label: 'Actions', disableSorting: true },
];

export default function Subscriptions() {
  const classes = useStyles();
  let history = useHistory();
  // Hooks State
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isValidCert, setIsValidCert] = useState(true);
  const [isCertChecked, setIsCertChecked] = useState(false);
  const [isILLIMITE, setIsILLIMITE] = useState(false);
  // const [isSrchDataEmpty, setIsSrchDataEmpty] = useState(false);
  const [subList, setSubList] = useState([]);
  const [resources, setResources] = useState([]);
  // const [records, setRecords] = useState(getAllSubscriptions());
  const [filterFn, setFilterFn] = useState({
    fn: (items) => {
      return items;
    },
  });
  const [openPopup, setOpenPopup] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: '',
    type: '',
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
  });

  const token = loadToken();

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getAppData({ token, history });
      setResources(data.resources);
      setSubList(data.subscriptions);
    } catch (err) {
      console.log(err);
    } finally {
      setIsCertChecked(true);
      setLoading(false);
    }
  };

  // Hooks Effect

  useEffect(() => {
    loadData();
  }, [token, history]);

  const putData = async (values) => {
    const res = await fetch(
      `${process.env.REACT_APP_URL_API}/subscriptions/${values.idAbonnement}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ values }),
      },
    );

    const data = await res.json();
    const { status, msg } = data;
    setNotify({
      isOpen: true,
      message: status === 'error' ? msg : 'Soumis avec succès',
      type: status,
    });

    invalidateAppData();
    const appData = await getAppData({ token, history, force: true });
    setResources(appData.resources);
    setSubList(appData.subscriptions);
    removeToken(res, history);
  };

  const postData = async (id, values) => {
    // Ajout d'un custom headers => ['X-For-Update'] pour différencier mes deux requêtes POST côté backend
    const res = await fetch(
      `${process.env.REACT_APP_URL_API}/subscriptions/${id}`,
      {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'X-For-Update': 'true',
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify({ values }),
      },
    );

    const data = await res.json();
    const { status, msg } = data;
    setNotify({
      isOpen: true,
      message: status === 'error' ? msg : 'Soumis avec succès',
      type: status,
    });

    invalidateAppData();
    const appData = await getAppData({ token, history, force: true });
    setResources(appData.resources);
    setSubList(appData.subscriptions);
    removeToken(res, history);
  };

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable(subList, headCells, filterFn);

  const handleSearch = (e) => {
    let target = e.target;
    setFilterFn({
      fn: (items) => {
        if (target.value === '') return items;
        return items.filter((item) =>
          checkUTF8(item.libelleRessource)
            .toLowerCase()
            .includes(target.value.toLowerCase()),
        );
      },
    });

    // Vérifie juste si la recherche ne corresponds pas et setter l'état
    // if (filterFn.fn(subList).length < 1) {
    //   setIsSrchDataEmpty(true);
    // } else {
    //   setIsSrchDataEmpty(false);
    // }
  };

  const addOrEdit = (subscription, resetForm) => {
    if (!recordForEdit) putData(subscription);
    else postData(subscription.idAbonnement, subscription);
    resetForm();
    setRecordForEdit(null);
    setOpenPopup(false);
  };

  const openInPopup = (item) => {
    if (item?.nbLicenceGlobale === 'ILLIMITE') {
      setIsILLIMITE(true);
    } else {
      setIsILLIMITE(false);
    }
    item.anneeScolaire = true;
    item.debutValidite = item.debutValidite.split('-').reverse().join('-');
    item.finValidite = item.finValidite.split('-').reverse().join('-');

    item.anneeDebut = new Date(`${item.debutValidite}`);
    item.anneeDebut = new Date(item.anneeDebut.getFullYear(), 11, 31, 23);
    item.anneeFin = new Date(`${item.anneeDebut.getFullYear() + 1}`);

    // Je transform publicCible en tableau pr pouvoir faire un Multiple Select
    if (!Array.isArray(item.publicCible)) {
      let itemWithoutSpace = item.publicCible
        .split(',')
        .map((itemPublic) => itemPublic.trim());
      item.publicCible = itemWithoutSpace;
    }

    setRecordForEdit(item);
    setOpenPopup(true);
  };

  const deleteData = async (id) => {
    const res = await fetch(
      `${process.env.REACT_APP_URL_API}/subscriptions/${id}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/xml;charset=utf-8',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (res.status === 204) {
      setConfirmDialog({
        ...confirmDialog,
        isOpen: false,
      });
      invalidateAppData();
      const appData = await getAppData({ token, history, force: true });
      setResources(appData.resources);
      setSubList(appData.subscriptions);
      setNotify({
        isOpen: true,
        message: 'Abonnement supprimé avec succès',
        type: 'success',
      });
    } else {
      const data = await res.json();
      setNotify({
        isOpen: true,
        message: data.msg,
        type: data.status,
      });
    }
  };

  // Rendu a afficher lorsque le certificat est invalide
  const SubscriptionDataEmpty = () => {
    return (
      <Paper className={classes.pageContent}>
        <div className={classes.inValidCert}>
          <Typography component='h1' variant='h2'>
            Votre certificat est invalide
          </Typography>
          <Typography component='h3' variant='h5'>
            Veuillez contacter votre service informatique
          </Typography>
        </div>
      </Paper>
    );
  };

  return (
    <>
      {!isCertChecked ? (
        <Paper className={classes.pageContent}>
          <div className={classes.root}>
            <CircularProgress />
          </div>
        </Paper>
      ) : isValidCert ? (
        <Paper className={classes.pageContent}>
          <Toolbar>
            <Controls.Input
              label="Recheche d'établissements"
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
              }}
              onChange={handleSearch}
            />
            <Controls.Button
              text='Ajouter un abonnement'
              variant='outlined'
              startIcon={<AddIcon />}
              className={classes.newButton}
              onClick={() => {
                setOpenPopup(true);
                setIsILLIMITE(false);
                setRecordForEdit(null);
              }}
            />
          </Toolbar>
          {loading ? (
            <div className={classes.root}>
              <CircularProgress />
            </div>
          ) : (
            <TblContainer>
              <TblHead />
              <TableBody>
                {recordsAfterPagingAndSorting().map((item) => (
                  <TableRow key={item.idAbonnement}>
                    {/* <TableRow key={item._id}> */}
                    <Tooltip title={item.libelleRessource}>
                      <TableCell>{checkUTF8(item.libelleRessource)}</TableCell>
                    </Tooltip>
                    <TableCell>{item.uaiEtab}</TableCell>
                    <TableCell>{item.debutValidite}</TableCell>
                    <TableCell>{item.finValidite}</TableCell>
                    <TableCell>{item.publicCible}</TableCell>
                    <TableCell>{item.nbLicenses}</TableCell>
                    <TableCell>{item.codeProjetRessource}</TableCell>
                    <TableCell>
                      <Controls.ActionButton
                        color='primary'
                        onClick={() => {
                          openInPopup(item);
                        }}
                      >
                        <EditOutlinedIcon fontSize='small' />
                      </Controls.ActionButton>
                      <Controls.ActionButton
                        color='secondary'
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            title:
                              'Êtes-vous sûr de vouloir supprimer cet abonnement ?',
                            subTitle:
                              'Vous ne pouvez pas annuler cette opération',
                            onConfirm: () => {
                              deleteData(item.idAbonnement);
                            },
                          });
                        }}
                      >
                        <CloseIcon fontSize='small' />
                      </Controls.ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TblContainer>
          )}
          <TblPagination />
        </Paper>
      ) : (
        <SubscriptionDataEmpty />
      )}
      <Box mt={8}>
        <Copyright />
      </Box>
      <Popup
        title="Formulaire d'abonnement"
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <SubscriptionsForm
          recordForEdit={recordForEdit}
          addOrEdit={addOrEdit}
          resources={resources}
          isILLIMITE={isILLIMITE}
        />
      </Popup>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </>
  );
}
